import { light } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { Fragment, useEffect, useRef, useState } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import Image from 'react-bootstrap/Image';
import useMobile from '../../hooks/useMobileHook';

export enum AvatarStatus {
  ENABLED = 100,
  DISABLED = 0.25,
}

export enum AvatarIndicator {
  SUCCESS = 'success-indicator',
  FAILED = 'failed-indicator',
  DEFAULT = 'default-indicator',
}

export enum AvatarSize {
  EXTRA_SMALL = 24,
  SMALL = 32,
  MEDIUM = 48,
  LARGE = 64,
}

export interface AvatarParams {
  id: string;
  image?: string | null;
  status: AvatarStatus;
  size: AvatarSize;
  indicator?: AvatarIndicator;
  name: string;
  hasTooltip?: boolean;
  tooltipClass?: string;
  onClick?: () => void;
}

const Avatar: React.FC<AvatarParams> = ({
  id,
  image,
  status,
  size,
  indicator,
  name,
  hasTooltip,
  tooltipClass,
  onClick,
}) => {
  const [indicatorClasses, setIndicatorClasses] = useState('');
  const [noAvatarText, setNoAvatarText] = useState({
    textSize: 0,
    isExtraSmall: false,
  });
  const [show, setShow] = useState(false);
  const [clickedAvatar, setClickedAvatar] = useState(false);
  const [target, setTarget] = useState(undefined);
  const ref = useRef(null);
  const isMobile = useMobile();

  const getIndicatorClass = (avatarSize: AvatarSize): string => {
    if (avatarSize === AvatarSize.EXTRA_SMALL) {
      return '';
    }
    const size = AvatarSize[avatarSize].toLowerCase();
    return `${size}-indicator`;
  };

  const getNameInitials = (name: string) => {
    return name
      ? name
          .split(' ')
          .map((n) => n[0])
          .join('')
      : '';
  };

  const getNoAvatarText = (avatarSize: AvatarSize): void => {
    if (avatarSize === AvatarSize.LARGE) {
      setNoAvatarText((noAvatarText) => ({ ...noAvatarText, textSize: 20 }));
    } else if (avatarSize === AvatarSize.MEDIUM) {
      setNoAvatarText((noAvatarText) => ({ ...noAvatarText, textSize: 14 }));
    } else if (avatarSize === AvatarSize.SMALL) {
      setNoAvatarText((noAvatarText) => ({ ...noAvatarText, textSize: 12 }));
    } else {
      setNoAvatarText({ textSize: 12, isExtraSmall: true });
    }
  };

  const handleOnToggle = (event: any) => {
    setShow(!show);
    setClickedAvatar(!clickedAvatar);
    setTarget(event.target);
  };

  useEffect(() => {
    setIndicatorClasses(`indicator ${getIndicatorClass(size)} ${indicator}`);

    if (!image) {
      getNoAvatarText(size);
    }
  }, [size, image, indicator]);

  return (
    <Fragment>
      {hasTooltip ? (
        <OverlayTrigger
          show={show}
          trigger={isMobile ? 'click' : undefined}
          rootClose={isMobile ? true : undefined}
          placement="top"
          target={target}
          onToggle={hasTooltip ? handleOnToggle : undefined}
          overlay={<Tooltip className={tooltipClass}>{name}</Tooltip>}
        >
          <div
            className={
              clickedAvatar
                ? 'position-relative avatar-click'
                : 'position-relative avatar-not-clicked'
            }
            style={{ width: size, height: size }}
            ref={ref}
          >
            {image ? (
              <Image
                src={`${process.env.PUBLIC_URL}/${image}`}
                className="rounded-circle"
                style={{
                  width: size,
                  opacity: status,
                }}
                alt={id}
              />
            ) : (
              <span
                className="no-img"
                style={{
                  width: size,
                  height: size,
                  fontSize: noAvatarText.textSize,
                }}
              >
                {getNameInitials(name).charAt(0)}
              </span>
            )}
            {indicator && (
              <span className={indicatorClasses}>
                {indicator === 'success-indicator' ? (
                  <FontAwesomeIcon
                    icon={light('check')}
                    className="status-icon"
                  />
                ) : indicator === 'failed-indicator' ? (
                  <FontAwesomeIcon
                    icon={light('close')}
                    className="status-icon"
                  />
                ) : (
                  ''
                )}
              </span>
            )}
          </div>
        </OverlayTrigger>
      ) : (
        <div
          className={
            clickedAvatar
              ? 'position-relative avatar-click'
              : 'position-relative avatar-not-clicked'
          }
          style={{ width: size, height: size }}
          onClick={onClick}
        >
          {image ? (
            <Fragment>
              <Image
                src={`${process.env.PUBLIC_URL}/${image}`}
                className="rounded-circle"
                style={{
                  width: size,
                  opacity: status,
                }}
                alt={id}
              />
            </Fragment>
          ) : (
            <span
              className="no-img"
              style={{
                width: size,
                height: size,
                fontSize: noAvatarText.textSize,
              }}
            >
              {noAvatarText.isExtraSmall
                ? getNameInitials(name).charAt(0)
                : getNameInitials(name)}
            </span>
          )}
          {indicator && (
            <span className={indicatorClasses}>
              {indicator === 'success-indicator' ? (
                <FontAwesomeIcon
                  icon={light('check')}
                  className="status-icon"
                />
              ) : indicator === 'failed-indicator' ? (
                <FontAwesomeIcon
                  icon={light('close')}
                  className="status-icon"
                />
              ) : (
                ''
              )}
            </span>
          )}
        </div>
      )}
    </Fragment>
  );
};

export default Avatar;
